import {
  getSeedPolygonCoordinates,
  type SeedGeometryLike,
} from "../utils/seedGeometry";

export interface SeedPlotFeatureProperties {
  id: string;
  replicant?: boolean;
  selectionColor?: string;
  selected?: boolean;
  disabled?: boolean;
  focused?: boolean;
}

interface SeedPlotGeometry extends SeedGeometryLike {
  geometries?: SeedGeometryLike[];
}

interface TrialPlotLike {
  plot?: {
    id?: number;
    rows?: number;
    columns?: number;
    isStripPlot?: boolean;
    geojson?: {
      geometry?: SeedPlotGeometry;
    };
  };
  replicants?: Array<{
    geojson?: {
      geometry?: SeedPlotGeometry;
    };
  }>;
}

export type SeedSelectionFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Polygon,
  SeedPlotFeatureProperties
>;

export interface RenderedSeedCell {
  id: string;
  geometry: GeoJSON.Geometry;
}

export const buildCellGeometryById = (
  cells: RenderedSeedCell[]
): Record<string, GeoJSON.Polygon | GeoJSON.MultiPolygon> => {
  const polygonsById = new Map<string, Map<string, GeoJSON.Position[][]>>();

  cells.forEach(({ id, geometry }) => {
    if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon") {
      return;
    }

    const polygons =
      polygonsById.get(id) ?? new Map<string, GeoJSON.Position[][]>();
    const coordinates =
      geometry.type === "Polygon"
        ? [geometry.coordinates]
        : geometry.coordinates;
    coordinates.forEach((polygon) =>
      polygons.set(JSON.stringify(polygon), polygon)
    );
    polygonsById.set(id, polygons);
  });

  return Object.fromEntries(
    [...polygonsById].map(([id, polygons]) => {
      const coordinates = [...polygons.values()];
      return [
        id,
        coordinates.length === 1
          ? { type: "Polygon", coordinates: coordinates[0] }
          : { type: "MultiPolygon", coordinates },
      ];
    })
  );
};

const polygonsForGeometry = (
  geometry: SeedGeometryLike | undefined
): GeoJSON.Position[][][] => {
  if (geometry === undefined) {
    return [];
  }

  try {
    return getSeedPolygonCoordinates(geometry);
  } catch {
    // Geometry may not be ready while the shared map builds its grid.
    // Rendered-cell capture remains available as a fallback.
    return [];
  }
};

const addPlotGeometry = (
  features: SeedSelectionFeatureCollection["features"],
  geometry: SeedPlotGeometry | undefined,
  groupId: number,
  rows: number,
  columns: number,
  targetMode: "plot" | "stripe",
  replicant = false
): void => {
  // A saved strip group has one geometry per stripe, not per polygon part.
  const cells =
    targetMode === "stripe" && geometry?.type === "GeometryCollection"
      ? (geometry.geometries ?? []).map(polygonsForGeometry)
      : polygonsForGeometry(geometry).map((coordinates) => [coordinates]);

  cells.slice(0, rows * columns).forEach((polygons, cellIndex) => {
    const row = Math.floor(cellIndex / columns);
    const column = cellIndex % columns;
    const id = `${groupId}-${row}-${column}`;

    polygons.forEach((coordinates) =>
      features.push({
        type: "Feature",
        id,
        geometry: {
          type: "Polygon",
          coordinates,
        },
        properties: {
          id,
          ...(replicant ? { replicant: true } : {}),
        },
      })
    );
  });
};

export const buildSeedPlotFeatureCollection = (
  trialPlots: TrialPlotLike[] | undefined,
  selectedIds: string[] = [],
  disabledIds: string[] = [],
  focusedId: string | null = null,
  selectionColor = "#0074d4",
  renderedCells: RenderedSeedCell[] = [],
  targetMode: "plot" | "stripe" = "plot"
): SeedSelectionFeatureCollection => {
  const selected = new Set(selectedIds);
  const disabled = new Set(disabledIds);

  const features: SeedSelectionFeatureCollection["features"] = [];

  (trialPlots ?? []).forEach((trialPlot) => {
    const plot = trialPlot.plot;
    const groupId = plot?.id;
    const rows = plot?.rows;
    const columns = plot?.columns;

    if (
      plot === undefined ||
      groupId === undefined ||
      rows === undefined ||
      columns === undefined ||
      (targetMode === "plot" && plot.isStripPlot === true) ||
      !Number.isInteger(rows) ||
      !Number.isInteger(columns) ||
      rows <= 0 ||
      columns <= 0
    ) {
      return;
    }

    addPlotGeometry(
      features,
      plot.geojson?.geometry,
      groupId,
      rows,
      columns,
      targetMode
    );

    trialPlot.replicants?.forEach((replicant) => {
      addPlotGeometry(
        features,
        replicant.geojson?.geometry,
        groupId,
        rows,
        columns,
        targetMode,
        true
      );
    });
  });

  renderedCells.forEach(({ id, geometry }) => {
    // GeoJSON.Geometry includes GeometryCollection, which has no
    // coordinates member. Only polygon geometry belongs in this overlay.
    if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon") {
      return;
    }

    polygonsForGeometry({
      type: geometry.type,
      coordinates: geometry.coordinates,
    }).forEach((coordinates) => {
      const alreadyPresent = features.some(
        (feature) =>
          feature.properties.id === id &&
          JSON.stringify(feature.geometry.coordinates) ===
            JSON.stringify(coordinates)
      );

      if (alreadyPresent) {
        return;
      }

      features.push({
        type: "Feature",
        id,
        geometry: {
          type: "Polygon",
          coordinates,
        },
        properties: {
          id,
        },
      });
    });
  });

  return {
    type: "FeatureCollection",
    features: features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        selectionColor,
        selected: selected.has(feature.properties.id),
        disabled: disabled.has(feature.properties.id),
        focused: feature.properties.id === focusedId,
      },
    })),
  };
};

export const buildSeedStripeFeatureCollection = (
  trialPlots: TrialPlotLike[] | undefined,
  renderedCells: RenderedSeedCell[] = []
): SeedSelectionFeatureCollection => {
  return buildSeedPlotFeatureCollection(
    trialPlots,
    [],
    [],
    null,
    "#0074d4",
    renderedCells,
    "stripe"
  );
};

export const buildViewportPolygon = (bounds: {
  getWest: () => number;
  getSouth: () => number;
  getEast: () => number;
  getNorth: () => number;
}): GeoJSON.Feature<GeoJSON.Polygon> => {
  const west = bounds.getWest();
  const south = bounds.getSouth();
  const east = bounds.getEast();
  const north = bounds.getNorth();

  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [west, south],
          [east, south],
          [east, north],
          [west, north],
          [west, south],
        ],
      ],
    },
  };
};
